/**
 * useBodyScrollLock (T27.2.5.3, D3) — lock `document.body` scroll while an
 * overlay (bottom sheet, modal) is open; restore the prior overflow on close.
 *
 * Why a shared, ref-counted hook:
 *   With a sheet open, dragging on the backdrop scrolls the page underneath —
 *   the "not stable" feeling D3 names. Setting `body { overflow: hidden }` on
 *   open fixes it. But two overlays can overlap (one sheet opens another, or a
 *   close animation overlaps the next open), and a naive "set on mount / restore
 *   on unmount" lets the first closer restore `overflow` while the second
 *   overlay is still open. A module-level ref count fixes that: only the FIRST
 *   lock captures + applies, only the LAST release restores. Intervening
 *   lock/unlock pairs just bump the counter.
 *
 * Usage: `useBodyScrollLock(open)` — pass the overlay's open flag. The lock
 * engages while `locked` is true and releases when it flips false or the
 * component unmounts.
 *
 * SSR/test-safe: no-ops when `document` is unavailable.
 */

import { useEffect } from 'react';

// Module-level lock state. Shared across every hook instance so concurrent
// overlays coordinate one body-overflow lock rather than fighting over it.
let lockCount = 0;
let previousOverflow: string | null = null;

function engageLock(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) {
    // First lock — capture the value we must restore, then hide overflow.
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount += 1;
}

function releaseLock(): void {
  if (typeof document === 'undefined') return;
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount === 0) {
    // Last lock released — restore the exact prior value (empty string clears
    // the inline style, matching the no-inline-overflow starting state).
    document.body.style.overflow = previousOverflow ?? '';
    previousOverflow = null;
  }
}

export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return undefined;
    engageLock();
    return releaseLock;
  }, [locked]);
}

/**
 * Test-only reset of the module-level lock state. Vitest shares module state
 * across tests in a file; a test that asserts the "first lock captures" path
 * must start from a clean counter. Not exported from the app barrel.
 */
export function __resetBodyScrollLockForTest(): void {
  lockCount = 0;
  previousOverflow = null;
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }
}
